import _ from 'lodash';
import React from 'react';
import { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Form, message } from 'antd';

import { SelectWithCreation } from '@/components/ui/Inputs';
import { useOWASContext } from '@/views/ListReport/Report/Owas/context';
import { useGetOWASReportTask } from '@/views/ListReport/Report/Owas/hooks/useGetOwasTasks';
import { useCreateOwasTask } from '@/views/ListReport/Report/Owas/hooks/useCreateOwasTask';
import { CustomFormItem } from '../../styles';

interface TaskNameProps {
	formName: string[];
}

const TASKS_LIMIT = 30;
const TASKS_OFFSET = 0;

const { useFormInstance } = Form;

export function TaskName({ formName }: Readonly<TaskNameProps>): JSX.Element {
	const form = useFormInstance();
	const { parameters, data } = useOWASContext();
	const [taskName, setTaskName] = useState<string>('');

	const title = I18n.get('Task name');
	const fieldName = [...formName, 'task_id'];

	const workstation_id = form.getFieldsValue(true).informations.workstation_id;

	const payload = {
		limit: TASKS_LIMIT,
		offset: TASKS_OFFSET,
		name: taskName || undefined,
		company_id: parameters?.company_id!!,
		organization_id: parameters?.organization_id!!,
		workstation_id: workstation_id ?? data?.workstation_id!!
	};

	const { isLoading: isCreatingTask, mutateAsync: createTask } = useCreateOwasTask();
	const { isFetching: isGettingTasks, data: tasks, isError: hasErrorGettingTasks } = useGetOWASReportTask(payload);

	function handleSelectTask(id: string): void {
		form.setFieldValue(fieldName, id);
		const task = tasks?.find((task) => task.id === id);
		form.setFieldValue([...formName, 'manual_tools'], task?.manual_tools);
		form.setFieldValue([...formName, 'manual_tools_input'], task?.manual_tools);
	}

	function getCreateTaskPayload(name: string) {
		return {
			name,
			company_id: parameters?.company_id!!,
			workstation_id: workstation_id ?? data?.workstation_id!!,
			organization_id: parameters?.organization_id!!
		};
	}

	async function handleOnCreateTask(name: string): Promise<void> {
		try {
			const payload = getCreateTaskPayload(name);
			const newTask = await createTask(payload);
			handleSelectTask(newTask.id);
		} catch (error) {
			message.error(I18n.get('Failed to create a new task'));
		}
	}

	const debounceSearch = _.debounce((name: string) => {
		setTaskName(name);
	}, 500);

	function handleSearchName(name: string): void {
		debounceSearch(name);
	}

	return (
		<Row>
			<Col span={24}>
				<CustomFormItem
					label={title}
					name={fieldName}
					labelCol={{ span: 24 }}
					rules={[{ required: true, message: I18n.get('Select the task name') }]}
				>
					<SelectWithCreation
						showSearch
						allowClear
						size="large"
						allowCreation
						placeholder={title}
						selectOptions={tasks}
						loading={isGettingTasks}
						onSearch={handleSearchName}
						onChange={handleSelectTask}
						onCreate={handleOnCreateTask}
						creatingData={isCreatingTask}
						disabled={hasErrorGettingTasks}
					/>
				</CustomFormItem>
			</Col>
		</Row>
	);
}
